// frontend/src/components/ProfileModal.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  X, BarChart3, Hash, Type, Calendar, AlertTriangle, CheckCircle2,
  ChevronDown, Loader2, Sparkles, Eye, ShieldCheck, Plus, Minus,
  Database, Percent, List, Search, Copy,
} from 'lucide-react';
import { apiClient } from '../api/client';
import { useLanguageStore } from '../store/useLanguageStore';

// ═══════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════

const categoryIcon = (cat) => {
  switch (cat) {
    case 'numeric': return <Hash className="w-3.5 h-3.5 text-blue-500" />;
    case 'date': return <Calendar className="w-3.5 h-3.5 text-purple-500" />;
    case 'text': return <Type className="w-3.5 h-3.5 text-emerald-500" />;
    default: return <Database className="w-3.5 h-3.5 text-slate-400" />;
  }
};

const patternLabel = (pattern, t) => {
  if (!pattern) return null;
  const labels = {
    email: t('profiling.pattern_email') || 'Email',
    phone: t('profiling.pattern_phone') || 'Teléfono',
    date: t('profiling.pattern_date') || 'Fecha',
    ruc: t('profiling.pattern_ruc') || 'RUC',
    dni: t('profiling.pattern_dni') || 'DNI',
    numeric: t('profiling.pattern_numeric') || 'Numérico',
  };
  return labels[pattern] || pattern;
};

const formatNumber = (n) => {
  if (n == null) return '—';
  if (typeof n === 'number') {
    if (Number.isInteger(n)) return n.toLocaleString('en-US');
    return n.toLocaleString('en-US', { maximumFractionDigits: 4 });
  }
  return String(n);
};

// ── Barra de frecuencia visual (CSS puro) ──
const FreqBar = ({ percent, color = 'bg-indigo-500' }) => (
  <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
    <div
      className={`h-full ${color} rounded-full transition-all`}
      style={{ width: `${Math.min(percent, 100)}%` }}
    />
  </div>
);

// ── Valor clickeable con copy-to-clipboard ──
const ClickableValue = ({ value, maxWidth = 'max-w-[120px]' }) => {
  const [copied, setCopied] = React.useState(false);
  const text = value != null ? String(value) : '';
  const isLong = text.length > 25;

  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* fallback silencioso */ }
  };

  if (!text) return <span className="italic text-slate-400">(vacío)</span>;

  return (
    <span
      onClick={handleCopy}
      className={`font-medium text-slate-700 truncate ${maxWidth} cursor-pointer
        hover:text-indigo-600 transition-colors inline-flex items-center gap-1 group`}
      title={isLong ? `${text}\n\n(click para copiar)` : text}
    >
      {text.length > 30 ? text.slice(0, 27) + '…' : text}
      {isLong && (
        <Copy className="w-2.5 h-2.5 opacity-0 group-hover:opacity-60 shrink-0 transition-opacity" />
      )}
      {copied && (
        <span className="text-[8px] text-emerald-600 font-bold ml-1 shrink-0">✓</span>
      )}
    </span>
  );
};

// ── Barra min-max con avg marcado (numéricos) ──
const RangeBar = ({ min, max, avg, t }) => {
  if (min == null || max == null || avg == null) return null;
  const range = max - min;
  if (range === 0) return null; // Todos iguales, no tiene sentido mostrar rango

  const avgPos = ((avg - min) / range) * 100;
  const clampedPos = Math.min(Math.max(avgPos, 4), 96);

  return (
    <div className="mt-3 mb-1">
      <p className="text-[9px] text-slate-400 uppercase tracking-wider mb-1">
        {t?.('profiling.range_visual') || 'Distribución'}
      </p>
      <div className="relative w-full h-6">
        {/* Track */}
        <div className="absolute top-2.5 left-0 right-0 h-1.5 bg-gradient-to-r from-blue-100 via-blue-200 to-blue-100 rounded-full" />
        {/* Min marker */}
        <div className="absolute top-1.5 left-0 w-1 h-3.5 bg-slate-300 rounded-full" />
        {/* Max marker */}
        <div className="absolute top-1.5 right-0 w-1 h-3.5 bg-slate-300 rounded-full" />
        {/* Avg marker */}
        <div
          className="absolute top-0.5 w-3 h-5 bg-indigo-500 rounded-sm shadow-sm border border-indigo-600"
          style={{ left: `calc(${clampedPos}% - 6px)` }}
          title={`Promedio: ${formatNumber(avg)}`}
        />
      </div>
      <div className="flex justify-between items-baseline mt-0.5 gap-2">
        <span className="text-[9px] text-slate-500 whitespace-nowrap">
          <span className="text-slate-400">min </span>
          <span className="font-bold">{formatNumber(min)}</span>
        </span>
        <span className="text-[9px] text-indigo-600 font-bold font-mono whitespace-nowrap">
          ▲ {formatNumber(avg)}
        </span>
        <span className="text-[9px] text-slate-500 whitespace-nowrap">
          <span className="font-bold">{formatNumber(max)}</span>
          <span className="text-slate-400"> max</span>
        </span>
      </div>
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════
// SUGGESTION CARD
// ═══════════════════════════════════════════════════════════════

const SuggestionCard = ({ colName, suggestions, t, accepted, onToggle }) => {
  if (!suggestions || suggestions.length === 0) return null;

  const ruleLabels = {
    NOT_NULL: t('data_rules.rule_NOT_NULL') || 'Campo obligatorio',
    REGEX: t('data_rules.rule_REGEX') || 'Formato válido',
    RANGE: t('data_rules.rule_RANGE') || 'Rango numérico',
    ALLOWED_VALUES: t('data_rules.rule_ALLOWED_VALUES') || 'Valores permitidos',
    UNIQUE: t('data_rules.rule_UNIQUE') || 'Sin duplicados',
    MIN_LENGTH: t('data_rules.rule_MIN_LENGTH') || 'Longitud mínima',
    MAX_LENGTH: t('data_rules.rule_MAX_LENGTH') || 'Longitud máxima',
  };

  const reasonLabels = {
    suggest_not_null: t('profiling.reason_not_null') || '0% nulos en la muestra',
    suggest_unique: t('profiling.reason_unique') || 'Todos los valores son únicos',
    suggest_regex_email: t('profiling.reason_regex_email') || 'Patrón de email detectado',
    suggest_regex_phone: t('profiling.reason_regex_phone') || 'Patrón de teléfono detectado',
    suggest_regex_ruc: t('profiling.reason_regex_ruc') || 'Patrón de RUC detectado',
    suggest_regex_dni: t('profiling.reason_regex_dni') || 'Patrón de DNI detectado',
    suggest_range_non_negative: t('profiling.reason_range_positive') || 'Todos los valores son ≥ 0',
    suggest_allowed_values: t('profiling.reason_allowed_values') || 'Pocos valores distintos encontrados',
  };

  return (
    <div className="space-y-1.5">
      {suggestions.map((s, idx) => {
        const key = `${colName}__${s.ruleType}__${idx}`;
        const isAccepted = accepted.has(key);
        return (
          <label
            key={key}
            className={`flex items-start gap-2.5 p-2 rounded-lg border cursor-pointer transition-all ${isAccepted
              ? 'bg-emerald-50 border-emerald-200'
              : 'bg-white border-slate-100 hover:border-slate-200'
              }`}
          >
            <input
              type="checkbox"
              checked={isAccepted}
              onChange={() => onToggle(key, colName, s)}
              className="mt-0.5 accent-emerald-600 shrink-0"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <ShieldCheck className={`w-3.5 h-3.5 shrink-0 ${isAccepted ? 'text-emerald-600' : 'text-slate-400'}`} />
                <span className={`text-xs font-semibold ${isAccepted ? 'text-emerald-800' : 'text-slate-700'}`}>
                  {ruleLabels[s.ruleType] || s.ruleType}
                </span>
                <span className="text-[9px] text-slate-400 bg-slate-50 px-1.5 py-0.5 rounded">
                  {Math.round(s.confidence * 100)}%
                </span>
              </div>
              <p className="text-[10px] text-slate-500 mt-0.5 leading-tight">
                {reasonLabels[s.reason] || s.reason}
              </p>
              {s.params?.pattern && (
                <code className="text-[9px] text-indigo-600 bg-indigo-50 px-1 py-0.5 rounded mt-1 inline-block font-mono">
                  {s.params.pattern}
                </code>
              )}
              {s.params?.values && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {s.params.values.slice(0, 8).map((v, i) => (
                    <span key={i} className="text-[9px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                      {String(v).length > 20 ? String(v).slice(0, 17) + '…' : v}
                    </span>
                  ))}
                  {s.params.values.length > 8 && (
                    <span className="text-[9px] text-slate-400 italic">+{s.params.values.length - 8}</span>
                  )}
                </div>
              )}
            </div>
          </label>
        );
      })}
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════
// EXPANDED ROW DETAIL
// ═══════════════════════════════════════════════════════════════

// ── Fila de stat: apilada en mobile, en línea con punteado en desktop ──
const StatRow = ({ label, children, color = 'text-slate-700' }) => (
  <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-1 text-[11px]">
    <span className="text-slate-500 whitespace-nowrap">{label}</span>
    <span className="hidden sm:block flex-1 border-b border-dotted border-slate-200 min-w-[12px] translate-y-[-2px]" />
    <span className={`font-bold ${color} shrink-0 sm:text-right whitespace-nowrap`}>{children}</span>
  </div>
);

const ColumnDetail = ({ col, t }) => {
  const cat = col.category;

  return (
    <div className="px-4 py-3 bg-slate-50/60 border-t border-slate-100 animate-in fade-in slide-in-from-top-1">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">

        {/* ── Bloque universal ── */}
        <div>
          <p className="text-[10px] font-bold text-slate-500 uppercase mb-2 tracking-wider">
            {t('profiling.general_stats') || 'General'}
          </p>
          <div className="space-y-1.5">
            <StatRow label={t('profiling.total_count') || 'Analizados'}>
              {formatNumber(col.totalCount)}
            </StatRow>
            <StatRow label={t('profiling.null_count') || 'Nulos/vacíos'} color="text-red-600">
              {formatNumber(col.nullCount)} ({col.nullPercent}%)
            </StatRow>
            <StatRow label={t('profiling.distinct') || 'Valores únicos'}>
              {formatNumber(col.distinctCount)} ({col.distinctPercent}%)
            </StatRow>
          </div>

          {/* Valores de muestra */}
          {col.sampleValues && col.sampleValues.length > 0 && (
            <div className="mt-3">
              <p className="text-[10px] font-bold text-slate-400 uppercase mb-1.5 tracking-wider">
                {t('profiling.sample_values') || 'Muestra'}
              </p>
              <div className="flex flex-wrap gap-1">
                {col.sampleValues.map((sv, i) => (
                  <span
                    key={i}
                    className="text-[10px] bg-white border border-slate-200 text-slate-600 px-2 py-0.5 rounded font-mono truncate max-w-[150px] cursor-pointer hover:border-indigo-300 hover:text-indigo-700 transition"
                    title={sv}
                    onClick={async (e) => {
                      e.stopPropagation();
                      try { await navigator.clipboard.writeText(sv); } catch { }
                    }}
                  >
                    {sv.length > 25 ? sv.slice(0, 22) + '…' : sv}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── Bloque numérico ── */}
        {cat === 'numeric' && (
          <div>
            <p className="text-[10px] font-bold text-blue-600 uppercase mb-2 tracking-wider">
              {t('profiling.numeric_stats') || 'Estadísticas numéricas'}
            </p>
            <div className="space-y-1.5">
              <StatRow label="Min">{formatNumber(col.min)}</StatRow>
              <StatRow label="Max">{formatNumber(col.max)}</StatRow>
              <StatRow label={t('profiling.average') || 'Promedio'}>{formatNumber(col.avg)}</StatRow>
              <StatRow label={t('profiling.median') || 'Mediana'}>{formatNumber(col.median)}</StatRow>
              {col.q1 != null && (
                <StatRow label="Q1 (25%)">{formatNumber(col.q1)}</StatRow>
              )}
              {col.q3 != null && (
                <StatRow label="Q3 (75%)">{formatNumber(col.q3)}</StatRow>
              )}
              {col.stddev != null && (
                <StatRow label={t('profiling.stddev') || 'Desv. estándar'}>{formatNumber(col.stddev)}</StatRow>
              )}
              <StatRow label={t('profiling.zeros') || 'Ceros'}>{formatNumber(col.zeros)}</StatRow>
            </div>
            <RangeBar min={col.min} max={col.max} avg={col.avg} t={t} />
          </div>
        )}

        {/* ── Bloque texto ── */}
        {(cat === 'text' || cat === 'unknown') && (
          <div>
            <p className="text-[10px] font-bold text-emerald-600 uppercase mb-2 tracking-wider">
              {t('profiling.text_stats') || 'Estadísticas de texto'}
            </p>
            <div className="space-y-1.5">
              <StatRow label={t('profiling.min_length') || 'Longitud mín.'}>{col.minLength}</StatRow>
              <StatRow label={t('profiling.max_length') || 'Longitud máx.'}>{col.maxLength}</StatRow>
              <StatRow label={t('profiling.avg_length') || 'Longitud prom.'}>{col.avgLength}</StatRow>
              <StatRow label={t('profiling.empty_strings') || 'Vacíos (trim)'}>{formatNumber(col.emptyCount)}</StatRow>
              {col.patternDetected && (
                <StatRow label={t('profiling.pattern_detected') || 'Patrón'}>
                  <span className="inline-flex items-center gap-1">
                    <span className="text-indigo-700 bg-indigo-50 px-1.5 py-0.5 rounded border border-indigo-100 text-[10px]">
                      {col.patternDetected}
                    </span>
                    {col.patternCoverage != null && (
                      <span className="text-slate-400 text-[10px]">
                        ({col.patternCoverage}%)
                      </span>
                    )}
                  </span>
                </StatRow>
              )}
            </div>
          </div>
        )}

        {/* ── Bloque fecha ── */}
        {cat === 'date' && (
          <div>
            <p className="text-[10px] font-bold text-purple-600 uppercase mb-2 tracking-wider">
              {t('profiling.date_stats') || 'Rango de fechas'}
            </p>
            <div className="space-y-1.5">
              <StatRow label={t('profiling.oldest') || 'Más antigua'}>{col.min || '—'}</StatRow>
              <StatRow label={t('profiling.newest') || 'Más reciente'}>{col.max || '—'}</StatRow>
            </div>
          </div>
        )}

        {/* ── Top Values ── */}
        <div className={cat === 'numeric' ? 'sm:col-span-2 lg:col-span-1' : ''}>
          <p className="text-[10px] font-bold text-slate-500 uppercase mb-2 tracking-wider">
            {t('profiling.top_values') || 'Top 10 valores'}
          </p>
          {col.topValues && col.topValues.length > 0 ? (
            <div className="space-y-1">
              {col.topValues.map((tv, i) => (
                <div key={i} className="flex items-center gap-2 text-[10px]">
                  <span className="text-slate-400 w-4 text-right shrink-0">{i + 1}.</span>
                  <ClickableValue value={tv.value} maxWidth="max-w-[140px]" />
                  <span className="text-slate-400 shrink-0">×{tv.count}</span>
                  <div className="flex-1 min-w-[40px]">
                    <FreqBar percent={tv.percent} />
                  </div>
                  <span className="text-slate-400 shrink-0 w-10 text-right">{tv.percent}%</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[10px] text-slate-400 italic">{t('common.no_data') || 'Sin datos'}</p>
          )}
        </div>
      </div>
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT: ProfileModal
// ═══════════════════════════════════════════════════════════════

export const ProfileModal = ({
  isOpen,
  onClose,
  sourceConfig,
  tableName,
  idConfigHead,
  onApplySuggestions,
  // ── S5.2: Mapping-aware mode ──
  mode = 'standalone',        // 'standalone' | 'mapping'
  mappedColumns = [],          // [{ sourceField, targetField, sourceTable? }]
  onAcceptRules = null,        // (rules: [{ targetField, rules: [...] }]) => void
}) => {
  const { t } = useLanguageStore();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [expandedCols, setExpandedCols] = useState(new Set());
  const [searchFilter, setSearchFilter] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [acceptedRules, setAcceptedRules] = useState(new Map()); // key → { colName, rule }

  // ── ESC para cerrar ──
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  // ── Ejecutar profiling ──
  const runProfile = useCallback(async () => {
    if (!tableName) return;
    // Necesita sourceConfig O idConfigHead
    if (!sourceConfig && !idConfigHead) return;

    setLoading(true);
    setError(null);
    setProfileData(null);
    setExpandedCols(new Set());
    setShowSuggestions(false);
    setAcceptedRules(new Map());

    try {
      // ── S5.2: En mapping mode, perfilar solo columnas mapeadas ──
      const mappingColumnsFilter = mode === 'mapping' && mappedColumns.length > 0
        ? mappedColumns.map(mc => {
          // Strip "tabla." prefix para obtener el nombre real de columna
          const src = mc.sourceField || '';
          return src.includes('.') ? src.split('.').slice(1).join('.') : src;
        })
        : null;

      const payload = idConfigHead
        ? {
          idConfigHead,
          engine: sourceConfig?.engine || '',
          config: { selectedTable: tableName },
          sampleSize: 10000,
          columns: mappingColumnsFilter,
          includeSuggestions: true,
        }
        : {
          engine: sourceConfig.engine || sourceConfig.type,
          config: {
            ...sourceConfig.config,
            selectedTable: tableName,
          },
          sampleSize: 10000,
          columns: mappingColumnsFilter,
          includeSuggestions: true,
        };

      const res = await apiClient.post('/onboarding/profile', payload);
      setProfileData(res.data);
      // S5.2: Auto-show suggestions in mapping mode
      if (mode === 'mapping') {
        setShowSuggestions(true);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const code = typeof detail === 'object' ? detail.code : detail;
      setError(code || t('common.error_server'));
    } finally {
      setLoading(false);
    }
  }, [sourceConfig, tableName, idConfigHead, t]);

  // Auto-ejecutar al abrir
  useEffect(() => {
    if (isOpen && sourceConfig && tableName) {
      runProfile();
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Toggle expanded row ──
  const toggleCol = (colName) => {
    setExpandedCols(prev => {
      const next = new Set(prev);
      if (next.has(colName)) next.delete(colName);
      else next.add(colName);
      return next;
    });
  };

  // ── Toggle suggestion acceptance ──
  const toggleAccepted = (key, colName, rule) => {
    setAcceptedRules(prev => {
      const next = new Map(prev);
      if (next.has(key)) next.delete(key);
      else next.set(key, { colName, rule });
      return next;
    });
  };

  // ── Aplicar reglas aceptadas ──
  const handleApplyRules = () => {
    if (acceptedRules.size === 0) return;

    // ── S5.2: Mapping mode → group by targetField, call onAcceptRules ──
    if (mode === 'mapping' && onAcceptRules) {
      const byTarget = {};
      for (const [, { colName, rule }] of acceptedRules) {
        const targetField = sourceToTargetMap[colName] || colName;
        if (!byTarget[targetField]) byTarget[targetField] = [];
        byTarget[targetField].push({
          type: rule.ruleType,
          mode: 'warn',
          config: rule.params || {},
        });
      }
      const result = Object.entries(byTarget).map(([targetField, rules]) => ({
        targetField,
        rules,
      }));
      onAcceptRules(result);
      onClose();
      return;
    }

    // ── Standalone mode (original) ──
    if (!onApplySuggestions) return;
    const byColumn = {};
    for (const [, { colName, rule }] of acceptedRules) {
      if (!byColumn[colName]) byColumn[colName] = [];
      byColumn[colName].push({
        type: rule.ruleType,
        mode: 'block',
        params: rule.params || {},
      });
    }
    onApplySuggestions(byColumn);
    onClose();
  };

  // ── S5.2: Build source→target lookup for mapping mode ──
  const sourceToTargetMap = useMemo(() => {
    if (mode !== 'mapping' || !mappedColumns.length) return {};
    const map = {};
    mappedColumns.forEach(mc => {
      // Strip prefix for matching against profiled column names
      const rawSource = mc.sourceField.includes('.')
        ? mc.sourceField.split('.').slice(1).join('.')
        : mc.sourceField;
      map[rawSource] = mc.targetField;
    });
    return map;
  }, [mode, mappedColumns]);

  if (!isOpen) return null;

  // ── Filtrar columnas ──
  const columns = profileData?.columns || [];
  const filteredCols = searchFilter
    ? columns.filter(c =>
      c.name.toLowerCase().includes(searchFilter.toLowerCase()) ||
      c.dataType.toLowerCase().includes(searchFilter.toLowerCase()) ||
      (c.patternDetected || '').toLowerCase().includes(searchFilter.toLowerCase())
    )
    : columns;

  const suggestions = profileData?.suggestions || {};
  const hasSuggestions = Object.keys(suggestions).length > 0;



  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center bg-black/40 backdrop-blur-sm overflow-y-auto py-6"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-5xl mx-4 my-auto animate-in fade-in slide-in-from-bottom-4">

        {/* ── HEADER ── */}
        <div className="flex items-center justify-between px-3 sm:px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center border border-indigo-100 shrink-0">
              <BarChart3 className="w-5 h-5 text-indigo-600" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-bold text-slate-900 truncate">
                {t('profiling.title') || 'Data Profiling'}
              </h2>
              {mode === 'mapping' && mappedColumns.length > 0 && (
                <p className="text-[10px] text-indigo-600 font-medium mt-0.5">
                  {(t('profiling.mapping_profile_mapped_only') || 'Analizando {count} campos mapeados')
                    .replace('{count}', mappedColumns.length)}
                </p>
              )}
              {profileData && (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500 mt-0.5">
                  <span className="font-medium text-slate-700 font-mono">{profileData.tableName}</span>
                  <span>
                    {t('profiling.total_rows') || 'Total'}:{' '}
                    <strong className="text-slate-700">
                      {profileData.totalRows != null ? formatNumber(profileData.totalRows) : (t('profiling.count_unavailable') || 'No disponible')}
                    </strong>
                    {profileData.totalRowsEstimated && profileData.totalRows != null && (
                      <span className="text-[9px] text-slate-400 ml-1">({t('profiling.estimated') || 'estimado'})</span>
                    )}
                  </span>
                  <span>
                    {t('profiling.sampled') || 'Muestra'}: <strong className="text-slate-700">{formatNumber(profileData.sampledRows)}</strong>
                  </span>
                  {profileData.truncated && (
                    <span className="inline-flex items-center gap-1 text-[9px] font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
                      <AlertTriangle className="w-3 h-3" />
                      {t('profiling.truncated') || 'Muestra parcial'}
                    </span>
                  )}
                  {profileData.totalRows == null && profileData.totalRowsEstimated && (
                    <span className="inline-flex items-center gap-1 text-[9px] font-bold text-slate-500 bg-slate-50 px-2 py-0.5 rounded border border-slate-200">
                      {t('profiling.count_unavailable') || 'Conteo no disponible'}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* ── BODY ── */}
        <div className="px-3 sm:px-6 py-4 max-h-[70vh] overflow-y-auto">

          {/* Loading */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-16 text-slate-500">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-500 mb-3" />
              <p className="text-sm font-medium">{t('profiling.analyzing') || 'Analizando datos...'}</p>
              <p className="text-xs text-slate-400 mt-1">{t('profiling.analyzing_desc') || 'Esto puede tomar unos segundos'}</p>
            </div>
          )}

          {/* Error */}
          {error && !loading && (
            <div className="flex flex-col items-center justify-center py-16">
              <AlertTriangle className="w-8 h-8 text-red-400 mb-3" />
              <p className="text-sm font-medium text-red-600">{t(`errors.${error}`) || error}</p>
              <button
                onClick={runProfile}
                className="mt-4 px-4 py-2 text-xs font-bold text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 transition"
              >
                {t('common.buttons.retry') || 'Reintentar'}
              </button>
            </div>
          )}

          {/* Results */}
          {profileData && !loading && (
            <>
              {/* Search + Suggest toggle */}
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    value={searchFilter}
                    onChange={e => setSearchFilter(e.target.value)}
                    placeholder={t('profiling.search_columns') || 'Buscar columna...'}
                    className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none transition"
                  />
                </div>
                {hasSuggestions && onApplySuggestions && (
                  <button
                    onClick={() => setShowSuggestions(!showSuggestions)}
                    className={`flex items-center gap-2 px-4 py-2 text-xs font-bold rounded-xl border transition ${showSuggestions
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                      : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                      }`}
                  >
                    <Sparkles className="w-4 h-4" />
                    {t('profiling.suggest_rules') || 'Sugerir reglas'}
                  </button>
                )}
              </div>

              {/* Column table */}
              <div className="border border-slate-200 rounded-xl overflow-hidden overflow-x-auto">
                <table className="w-full min-w-[700px]">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="px-3 py-2.5 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider w-8" />
                      <th className="px-3 py-2.5 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                        {t('profiling.column') || 'Columna'}
                      </th>
                      <th className="px-3 py-2.5 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                        {t('common.type') || 'Tipo'}
                      </th>
                      <th className="px-3 py-2.5 text-center text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                        {t('profiling.null_percent') || 'Nulos %'}
                      </th>
                      <th className="px-3 py-2.5 text-center text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                        {t('profiling.distinct') || 'Únicos %'}
                      </th>
                      <th className="px-3 py-2.5 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                        {t('profiling.top_value') || 'Top valor'}
                      </th>
                      <th className="px-3 py-2.5 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                        {t('profiling.pattern_detected') || 'Patrón'}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCols.map((col) => {
                      const isExpanded = expandedCols.has(col.name);
                      const colSuggestions = suggestions[col.name] || [];
                      const nullColor = col.nullPercent === 0
                        ? 'text-emerald-600'
                        : col.nullPercent > 50
                          ? 'text-red-600'
                          : col.nullPercent > 10
                            ? 'text-amber-600'
                            : 'text-slate-600';

                      return (
                        <React.Fragment key={col.name}>
                          <tr
                            onClick={() => toggleCol(col.name)}
                            className={`border-b border-slate-100 cursor-pointer transition-colors ${isExpanded ? 'bg-slate-50' : 'hover:bg-slate-50/50'
                              }`}
                          >
                            <td className="px-3 py-2.5 text-center">
                              {isExpanded
                                ? <Minus className="w-3.5 h-3.5 text-indigo-500" />
                                : <Plus className="w-3.5 h-3.5 text-slate-400" />
                              }
                            </td>
                            <td className="px-3 py-2.5">
                              <div className="flex items-center gap-2">
                                {categoryIcon(col.category)}
                                <span className="text-xs font-semibold text-slate-800 font-mono">{col.name}</span>
                                {mode === 'mapping' && sourceToTargetMap[col.name] && (
                                  <span className="text-[9px] font-mono text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-100">
                                    → {sourceToTargetMap[col.name]}
                                  </span>
                                )}
                                {col.isPk && (
                                  <span className="text-[8px] font-bold text-amber-700 bg-amber-50 px-1 py-0.5 rounded border border-amber-200">PK</span>
                                )}
                                {col.isFk && (
                                  <span className="text-[8px] font-bold text-purple-700 bg-purple-50 px-1 py-0.5 rounded border border-purple-200">FK</span>
                                )}
                                {showSuggestions && colSuggestions.length > 0 && (
                                  <span className="text-[8px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200">
                                    <Sparkles className="w-2.5 h-2.5 inline mr-0.5" />
                                    {colSuggestions.length}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-2.5 text-[11px] text-slate-500 font-mono">
                              {col.dataType}
                              {/* Mostrar longitud si no está ya en el tipo */}
                              {col.category === 'text' && col.maxLength != null && !col.dataType.includes('(') && (
                                <span className="text-slate-400">({col.maxLength})</span>
                              )}
                            </td>
                            <td className="px-3 py-2.5 text-center">
                              <span className={`text-xs font-bold ${nullColor}`}>{col.nullPercent}%</span>
                            </td>
                            <td className="px-3 py-2.5 text-center">
                              <span className="text-xs font-bold text-slate-600">{col.distinctPercent}%</span>
                            </td>
                            <td className="px-3 py-2.5">
                              {col.topValues?.[0] ? (
                                <span className="text-[11px] text-slate-600 flex items-center gap-1">
                                  <ClickableValue value={col.topValues[0].value} maxWidth="max-w-[100px]" />
                                  <span className="text-slate-400 shrink-0">({col.topValues[0].percent}%)</span>
                                </span>
                              ) : (
                                <span className="text-[11px] text-slate-300">—</span>
                              )}
                            </td>
                            <td className="px-3 py-2.5">
                              {col.patternDetected ? (
                                <span className="text-[10px] font-bold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-100">
                                  {patternLabel(col.patternDetected, t)}
                                </span>
                              ) : (
                                <span className="text-[10px] text-slate-300 italic">{t('profiling.no_pattern') || '—'}</span>
                              )}
                            </td>
                          </tr>

                          {/* Expanded detail */}
                          {isExpanded && (
                            <tr>
                              <td colSpan={7} className="p-0">
                                <ColumnDetail col={col} t={t} />
                                {/* Suggestions for this column */}
                                {showSuggestions && colSuggestions.length > 0 && (
                                  <div className="px-4 pb-3 bg-slate-50/60">
                                    <p className="text-[10px] font-bold text-emerald-600 uppercase mb-2 tracking-wider flex items-center gap-1">
                                      <Sparkles className="w-3 h-3" />
                                      {t('profiling.suggested_rules') || 'Reglas sugeridas'}
                                    </p>
                                    <SuggestionCard
                                      colName={col.name}
                                      suggestions={colSuggestions}
                                      t={t}
                                      accepted={new Set([...acceptedRules.keys()])}
                                      onToggle={toggleAccepted}
                                    />
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}

                    {filteredCols.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-6 py-8 text-center text-sm text-slate-400 italic">
                          {searchFilter
                            ? (t('profiling.no_match') || 'Sin coincidencias')
                            : (t('common.no_data') || 'Sin datos')
                          }
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* ── FOOTER ── */}
        <div className="flex items-center justify-between px-3 sm:px-6 py-4 border-t border-slate-100 bg-slate-50/50 rounded-b-2xl">
          <div className="text-[11px] text-slate-500">
            {profileData && (
              <span>
                {filteredCols.length} {t('profiling.columns_shown') || 'columnas'}
                {searchFilter && ` (${t('profiling.filtered') || 'filtrado'})`}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {showSuggestions && acceptedRules.size > 0 && (onApplySuggestions || (mode === 'mapping' && onAcceptRules)) && (
              <button
                onClick={handleApplyRules}
                className="px-5 py-2 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl shadow-sm transition flex items-center gap-2"
              >
                <CheckCircle2 className="w-4 h-4" />
                {t('profiling.apply_rules') || 'Aplicar'} ({acceptedRules.size})
              </button>
            )}
            <button
              onClick={onClose}
              className="px-5 py-2 text-xs font-bold text-slate-700 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition"
            >
              {t('common.buttons.close') || 'Cerrar'}
            </button>
          </div>
        </div>
      </div >
    </div >
  );
};
